// New transfer (CR_20260915_Request_Qty R0, §5.1): opened from the Assets page and from each warehouse / project
// Transfers tab. A transfer starts at a place the person works at and goes to a place they may send to
// (CR_Manager_vs_User_role P18): a project user sends back to any warehouse; the other side is shown by name only.
// `fromIds` limits From (a warehouse tab: that place; a project: its locations); `initialFrom` pre-fills it.
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { groupPlaces, transferEndKey } from '@fleetspoke/core/places';
import type { Whoami } from '@fleetspoke/core/types';
import { canManage, useSession } from '../auth/session';
import { type SiteOverview, useSiteBoard, useSites } from '../data/queries';
import { type TransferPlace, useCreateTransfer, useTransferPlaces } from '../data/workflow';
import { canActAt } from '../lib/zones';
import { blockOf, DispatchBlockNotice } from './DispatchBlock';

/** The visible sites as transfer places: what the server's transfer_places() returns before it exists. */
export const asPlaces = (sites: SiteOverview[] | undefined): TransferPlace[] | undefined =>
  sites?.map((s) => ({
    site_id: s.site_id,
    name: s.name,
    type: s.type,
    project_id: s.project_id,
    project_name: null,
    is_active: s.is_active,
    is_mine: true,
  }));

/** Names of both ends of a transfer: the caller's places and the other side (by name only). */
/**
 * A place's name by id. With nothing to name it says why (2026-09-27): a transfer's missing `from` / `to` end or a
 * place the viewer may not see (transferEndKey); `side` defaults to `to` for callers that only name a destination.
 */
export function usePlaceNames() {
  const { t } = useTranslation();
  const sites = useSites();
  const places = useTransferPlaces(asPlaces(sites.data));
  return (id: string | null, side: 'from' | 'to' = 'to') =>
    (id &&
      (places.data?.find((p) => p.site_id === id)?.name ??
        sites.data?.find((s) => s.site_id === id)?.name)) ||
    t(transferEndKey(id, side));
}

/** Options grouped Warehouses · Projects · Other locations (the same grouping as the phone). */
function placeOptions(places: TransferPlace[], t: (k: string) => string) {
  const projects = [
    ...new Map(
      places.filter((p) => p.project_id).map((p) => [p.project_id!, p.project_name ?? '—']),
    ),
  ].map(([id, name]) => ({ id, name }));
  const groups = groupPlaces(places, projects);
  const items = (list: TransferPlace[], label: (p: TransferPlace) => string) =>
    list.map((p) => (
      <MenuItem key={p.site_id} value={p.site_id}>
        {label(p)}
      </MenuItem>
    ));
  return [
    ...(groups.warehouses.length
      ? [
          <ListSubheader key="w">{t('nav.warehouses')}</ListSubheader>,
          ...items(groups.warehouses, (p) => p.name),
        ]
      : []),
    ...(groups.projects.length
      ? [
          <ListSubheader key="p">{t('nav.projects')}</ListSubheader>,
          ...groups.projects.flatMap((g) =>
            items(g.locations, (p) =>
              g.locations.length > 1 ? `${g.project.name} · ${p.name}` : g.project.name,
            ),
          ),
        ]
      : []),
    ...(groups.other.length
      ? [
          <ListSubheader key="o">{t('places.group_other')}</ListSubheader>,
          ...items(groups.other, (p) => p.name),
        ]
      : []),
  ];
}

/** Active places among `fromIds` (all when omitted) that the person can dispatch from. */
export function dispatchableFrom<T extends { site_id: string; is_active: boolean }>(
  me: Whoami | undefined,
  places: readonly T[],
  fromIds?: readonly string[],
): T[] {
  return places.filter(
    (p) => p.is_active && canActAt(me, p.site_id) && (!fromIds || fromIds.includes(p.site_id)),
  );
}

export function NewTransferDialog({
  onClose,
  fromIds,
  initialFrom,
}: {
  onClose(): void;
  fromIds?: readonly string[];
  initialFrom?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me } = useSession();
  const sites = useSites();
  const places = useTransferPlaces(asPlaces(sites.data));
  const fromOptions = dispatchableFrom(me, places.data ?? [], fromIds);
  const defaultFrom =
    initialFrom && fromOptions.some((p) => p.site_id === initialFrom)
      ? initialFrom
      : fromIds && fromOptions.length === 1
        ? fromOptions[0]!.site_id
        : '';
  const [chosen, setChosen] = useState<string | null>(null);
  const from = chosen ?? defaultFrom;
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const board = useSiteBoard(from);
  const create = useCreateTransfer();
  const block = blockOf(create.error);
  // a refused dispatch can be sent again by a manager with a reason (CR_20260921 L6a)
  const send = (overrideReason: string | null) =>
    create.mutate(
      { fromSiteId: from, toSiteId: to, assetIds: [...picked], note, overrideReason },
      { onSuccess: (r) => void navigate(`/transfers/${r.transfer_id}`) },
    );
  const candidates = (board.data ?? []).filter(
    (a) => a.custody_site_id === from && !a.open_transfer_id,
  );
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('transfers.new')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              fullWidth
              label={t('transfers.from')}
              value={fromOptions.some((p) => p.site_id === from) ? from : ''}
              onChange={(e) => {
                setChosen(e.target.value);
                setPicked(new Set());
                if (e.target.value === to) setTo('');
              }}
              data-testid="transfer-from"
            >
              {placeOptions(fromOptions, t)}
            </TextField>
            <TextField
              select
              fullWidth
              label={t('transfers.to')}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="transfer-to"
            >
              {placeOptions(
                (places.data ?? []).filter((p) => p.is_active && p.site_id !== from),
                t,
              )}
            </TextField>
          </Stack>
          {from ? (
            <Stack sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {candidates.map((a) => (
                <FormControlLabel
                  key={a.asset_id}
                  // out of service stays listed, greyed with its reason — hidden, it would be reported missing (L15)
                  disabled={a.service_state === 'out_of_service'}
                  control={
                    <Checkbox
                      checked={picked.has(a.asset_id)}
                      onChange={(e) =>
                        setPicked((p) => {
                          const next = new Set(p);
                          if (e.target.checked) next.add(a.asset_id);
                          else next.delete(a.asset_id);
                          return next;
                        })
                      }
                    />
                  }
                  label={`${a.asset_code}${a.description ? ` · ${a.description}` : ''}${
                    a.service_state === 'out_of_service'
                      ? ` · ${t('service.greyed')}${a.service_reason ? ` — ${a.service_reason}` : ''}`
                      : ''
                  }`}
                />
              ))}
              {!candidates.length && !board.isLoading ? (
                <Typography color="text.secondary">{t('transfers.nothing_to_move')}</Typography>
              ) : null}
            </Stack>
          ) : null}
          <TextField
            label={`${t('transfers.note')} (${t('common.optional')})`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {block ? (
            <DispatchBlockNotice
              block={block}
              manager={canManage(me)}
              pending={create.isPending}
              onOverride={(reason) => send(reason)}
            />
          ) : create.error ? (
            <Alert severity="error">{(create.error as Error).message}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!from || !to || !picked.size || create.isPending}
          onClick={() => send(null)}
        >
          {t('transfers.dispatch', { count: picked.size })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The New transfer button of an entry point (the Assets page, each place's header), shown only to someone who can
 * dispatch from one of the allowed From places (a project user on their project, never on a warehouse, §5.1). It
 * reads "Transfers" (owner, 2026-09-15); the dialog it opens keeps the title "New transfer".
 */
export function NewTransferButton({
  fromIds,
  initialFrom,
  variant = 'outlined',
}: {
  fromIds?: readonly string[];
  initialFrom?: string;
  variant?: 'outlined' | 'contained';
}) {
  const { t } = useTranslation();
  const { me } = useSession();
  const sites = useSites();
  const [open, setOpen] = useState(false);
  if (!dispatchableFrom(me, sites.data ?? [], fromIds).length) return null;
  return (
    <>
      <Button variant={variant} onClick={() => setOpen(true)} data-testid="new-transfer">
        {t('transfers.title')}
      </Button>
      {open ? (
        <NewTransferDialog
          onClose={() => setOpen(false)}
          fromIds={fromIds}
          initialFrom={initialFrom}
        />
      ) : null}
    </>
  );
}
